'use strict';

var _ = require('lodash');
var SquareIndicators = function SquareIndicators(d3Helper, OverlappingResolutionService) {
  /**
   *
   * @param xPropertyName - a property name used to find X value
   * @param yPropertyName - a property name used to find Y value
   * @param dataPropertyName - a property name used to find data collection
   * @param labelPropertyName - a property name used to display a label in indicator
   * @param sortingBy - array of data points property names used for sorting labels
   * @param sortingOrder - array of sorting directions
   * @returns {Function}
   * @constructor
   */
  return function (xPropertyName, yPropertyName, dataPropertyName, labelPropertyName, sortingBy, sortingOrder) {
    var params,
      scales,
      clipPath,
      INDICATOR_SIZE = 16,
      HALF_INDICATOR_SIZE = INDICATOR_SIZE / 2,
      MARGIN = 2,
      CORNER_RADIUS = 3;

    /**
     * Draws colored rectangle
     * @param containerGroup
     * @param dataPoint
     */
    function drawSingleIndicatorBox(containerGroup, dataPoint) {
      containerGroup.append('rect').attr({
        rx: dataPoint.rx,
        ry: dataPoint.ry,
        x: dataPoint.textX - HALF_INDICATOR_SIZE,
        y: dataPoint.textY - HALF_INDICATOR_SIZE,
        width: INDICATOR_SIZE,
        height: INDICATOR_SIZE,
        "class": dataPoint.className
      });
    }

    /**
     * Draws black border rectangle
     * @param groupContainer
     * @param dataPoint
     */
    function drawBorderRect(groupContainer, dataPoint) {
      groupContainer.append('rect').attr({
        rx: dataPoint.rx,
        ry: dataPoint.ry,
        x: dataPoint.x,
        y: dataPoint.y,
        width: dataPoint.width,
        height: dataPoint.height,
        "class": 'background-rect'
      });
    }

    /**
     * Draws text label inside indicator
     * @param groupContainer
     * @param dataPoint
     */
    function drawTexLabel(groupContainer, dataPoint) {
      groupContainer.append('text').attr({
        x: dataPoint.textX,
        y: dataPoint.textY,
        "class": dataPoint.className,
        dy: '.35em'
      }).text(dataPoint.label);
    }
    function drawSquareIndicators(groupContainer, dataPoints) {
      groupContainer.selectAll('*').remove();
      dataPoints.forEach(function (d, index) {
        if (d.labels) {
          var multiLineIndicatorGroup = d3Helper.createGroupElement(groupContainer, 'multi-line-indicator index-' + index);
          drawBorderRect(multiLineIndicatorGroup, d);
          _.forEach(d.labels, function (labelData) {
            drawSingleIndicatorBox(multiLineIndicatorGroup, labelData);
            drawTexLabel(multiLineIndicatorGroup, labelData);
          });
        } else {
          var singleLineIndicatorGroup = d3Helper.createGroupElement(groupContainer, 'single-line-indicator index-' + index);
          drawBorderRect(singleLineIndicatorGroup, d);
          drawSingleIndicatorBox(singleLineIndicatorGroup, d);
          drawTexLabel(singleLineIndicatorGroup, d);
        }
      });
    }

    /**
     * Shifts X coordinate if it's too close from left or right edge
     * @param x
     * @return {*}
     */
    function shiftCoordinateX(x) {
      if (Math.abs(0 - x) < INDICATOR_SIZE) {
        return HALF_INDICATOR_SIZE + MARGIN;
      }
      if (Math.abs(params.width - x) < INDICATOR_SIZE) {
        return params.width - HALF_INDICATOR_SIZE - MARGIN;
      }
      return x;
    }

    /**
     * Decorates dataPoints with different display attributes
     * @param dataPoints
     */
    function decorateWithDisplayAttributes(dataPoints) {
      _.forEach(dataPoints, function (dataPoint) {
        var originalX = scales.xScale(dataPoint[xPropertyName]);
        var originalY = scales.yScale(dataPoint[yPropertyName]);
        var shiftedX = shiftCoordinateX(originalX);
        _.assign(dataPoint, {
          rx: CORNER_RADIUS,
          ry: CORNER_RADIUS,
          originalX: originalX,
          originalY: originalY,
          x: shiftedX - HALF_INDICATOR_SIZE - MARGIN,
          y: originalY - HALF_INDICATOR_SIZE - MARGIN,
          textX: shiftedX,
          textY: originalY,
          width: INDICATOR_SIZE + 2 * MARGIN,
          height: INDICATOR_SIZE + 2 * MARGIN,
          className: dataPoint.squareIndicatorsType,
          label: dataPoint[labelPropertyName]
        });
      });
    }

    /**
     * Entry point for overlapping resolution algorithm
     * @param dataPoints
     */
    function resolveOverlapping(dataPoints) {
      OverlappingResolutionService.resolveOverlapping({
        dataPoints: dataPoints,
        sortingBy: sortingBy,
        sortingOrder: sortingOrder,
        labelPropertyName: labelPropertyName,
        xPropertyName: xPropertyName,
        yPropertyName: yPropertyName,
        indicatorSizing: {
          INDICATOR_SIZE: INDICATOR_SIZE,
          HALF_INDICATOR_SIZE: HALF_INDICATOR_SIZE,
          MARGIN: MARGIN
        },
        params: params
      });
    }

    /**
     * Get only series with square indicators and from each series all displayable data points
     * @param data
     * @return {Array}
     */
    function getDisplayableDataPoints(data) {
      var dataPoints = [];
      var indicatorsSeries = _.cloneDeep(_.filter(data, 'squareIndicatorsType'));
      _.forEach(indicatorsSeries, function (dataSet) {
        dataPoints = _.concat(dataPoints, _.filter(dataSet[dataPropertyName], 'squareIndicatorsType'));
      });
      return dataPoints;
    }
    var squaresFunc = function squaresFunc(container) {
      var group = d3Helper.createGroupElement(container, 'group-square-indicators');
      if (clipPath) {
        group.attr('clip-path', clipPath);
      }
      var dataPoints = getDisplayableDataPoints(group.datum());
      decorateWithDisplayAttributes(dataPoints);
      resolveOverlapping(dataPoints);
      drawSquareIndicators(group, dataPoints);
    };

    /**
     * Sets/gets square indicators params
     * @param value
     * @returns {squaresFunc}
     */
    squaresFunc.params = function (value) {
      params = value;
      return squaresFunc;
    };

    /**
     * Sets/gets square indicators scales
     * @param value
     * @returns {squaresFunc}
     */
    squaresFunc.scales = function (value) {
      scales = value;
      return squaresFunc;
    };

    /**
     * Sets/get clip path
     * @param value
     * @return {squaresFunc}
     */
    squaresFunc.clipPath = function (value) {
      clipPath = 'url(' + value + ')';
      return squaresFunc;
    };
    return squaresFunc;
  };
};
SquareIndicators.$inject = ['d3Helper', 'OverlappingResolutionService'];
module.exports = SquareIndicators;